// src/features/products/pages/BulkProductUploadPage.tsx
// ADVANCED PRODUCT IMPORT HUB — PURE LIGHT MODE, FINANCIAL PROJECTIONS & DEMO DATA

import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { BulkUploadProgress } from '@/components/bulk-upload/BulkUploadProgress';
import { BulkUploadResultModal } from '../components/BulkUploadResultModal';
import { downloadTemplate, bulkUploadProducts, previewBulkUpload, BulkUploadResult, BulkUploadPreview } from '../services/productsApi';
import {
    ArrowLeft, Upload, Download, FileSpreadsheet, CheckCircle,
    AlertTriangle, Info, Package, FileText, X,
    CloudUpload, Table2, Search, HelpCircle,
    Eye, Zap, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    TrendingUp, DollarSign, Copy, Check, Sparkles, Filter,
    PlayCircle, ShieldCheck
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import React from 'react';

// ============================================================
// CONSTANTS & TEMPLATE SCHEMA
// ============================================================

interface ColumnGuide {
    col: string;
    name: string;
    required: boolean;
    example: string;
    desc: string;
}

const TEMPLATE_COLUMNS: ColumnGuide[] = [
    { col: 'A', name: 'Product Name', required: true, example: 'Indomie Instant Noodles 70g', desc: 'Full product title. Must be unique in your catalog.' },
    { col: 'B', name: 'Category Slug', required: true, example: 'groceries-pasta', desc: 'Slug of category. Auto-created if it does not exist.' },
    { col: 'C', name: 'Vendor Name', required: false, example: 'Dufil Prima Foods', desc: 'Supplier name. Automatically added to your vendor directory if not existing.' },
    { col: 'D', name: 'Description', required: false, example: 'Chicken flavour instant noodles', desc: 'Detailed product description.' },
    { col: 'E', name: 'Brand', required: false, example: 'Indomie', desc: 'Brand or manufacturer name.' },
    { col: 'F', name: 'Retail Price', required: true, example: '250.00', desc: 'Selling price at point of sale.' },
    { col: 'G', name: 'Cost Price', required: true, example: '190.00', desc: 'Acquisition / purchase cost.' },
    { col: 'H', name: 'Tax Rate (%)', required: false, example: '7.50', desc: 'VAT or sales tax rate. Default: 7.5%' },
    { col: 'I', name: 'SKU', required: false, example: 'IND-CHK-70G', desc: 'Internal SKU. Auto-generated if left blank.' },
    { col: 'J', name: 'Barcode', required: false, example: '8998866200115', desc: 'Standard EAN/UPC barcode. Auto-generated if blank.' },
    { col: 'K', name: 'Base UOM Name', required: true, example: 'Piece', desc: 'Primary sales unit (e.g. Piece, Pack, Bottle).' },
    { col: 'L', name: 'Base UOM Abbr', required: true, example: 'pc', desc: 'Unit abbreviation (e.g. pc, pk, btl).' },
];

type UploadStep = 'upload' | 'preview' | 'importing' | 'complete';
const PREVIEW_PAGE_SIZE = 25;

// ============================================================
// COLUMN GUIDE MODAL (LIGHT THEMED)
// ============================================================

function ColumnGuideModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    const [copied, setCopied] = useState(false);

    if (!isOpen) return null;

    const sampleCsv = `Product Name,Category Slug,Vendor Name,Description,Brand,Retail Price,Cost Price,Tax Rate (%),SKU,Barcode,Base UOM Name,Base UOM Abbr
"Indomie Chicken 70g","noodles","Dufil Prima Foods","Instant noodles chicken flavor","Indomie",250.00,190.00,7.50,"IND-70G","899886620011","Piece","pc"
"Golden Penny Pasta 500g","groceries","Flour Mills of Nigeria","Spaghetti pasta 500g","Golden Penny",800.00,650.00,7.50,"GP-PAST-500","779886620022","Pack","pk"`;

    const handleCopy = () => {
        navigator.clipboard.writeText(sampleCsv);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs" onClick={onClose}>
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            >
                <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-700">
                            <Table2 className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-bold text-slate-900 text-lg">Spreadsheet Column Reference</h3>
                            <p className="text-xs text-slate-500">12 supported columns for seamless catalog and vendor imports</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto space-y-4">
                    <div className="flex items-center justify-between bg-emerald-50/60 p-3.5 rounded-2xl border border-emerald-200/80">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            <span className="text-xs text-emerald-900 font-medium">Want to test with sample CSV rows?</span>
                        </div>
                        <button
                            onClick={handleCopy}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-300 rounded-xl text-xs font-semibold text-emerald-800 hover:bg-emerald-50 transition-all shadow-xs"
                            type="button"
                        >
                            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                            {copied ? 'Copied CSV!' : 'Copy Sample CSV'}
                        </button>
                    </div>

                    <div className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden">
                        {TEMPLATE_COLUMNS.map((col) => (
                            <div key={col.col} className="p-3.5 flex items-start gap-3 hover:bg-slate-50 transition-colors">
                                <span className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-800 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                    {col.col}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm font-bold text-slate-800">{col.name}</p>
                                        {col.required ? (
                                            <span className="text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full">
                                                Required
                                            </span>
                                        ) : (
                                            <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                                                Optional
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{col.desc}</p>
                                    <p className="text-[11px] font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded mt-1.5 inline-block">
                                        Example: {col.example}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-semibold transition"
                        type="button"
                    >
                        Close Reference
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

// ============================================================
// PREVIEW TABLE COMPONENT (LIGHT MODE)
// ============================================================

function PreviewTable({ previewData, previewFilter, previewSearch, expandedRow, onFilterChange, onSearchChange, onToggleExpand }: {
    previewData: BulkUploadPreview;
    previewFilter: 'all' | 'valid' | 'warning' | 'error';
    previewSearch: string;
    expandedRow: number | null;
    onFilterChange: (filter: 'all' | 'valid' | 'warning' | 'error') => void;
    onSearchChange: (search: string) => void;
    onToggleExpand: (row: number | null) => void;
}) {
    const [page, setPage] = useState(1);

    const allFiltered = useMemo(() => {
        return previewData.preview.filter(row => {
            if (previewFilter !== 'all' && row.status !== previewFilter) return false;
            if (previewSearch) {
                const term = previewSearch.toLowerCase();
                return row.name.toLowerCase().includes(term) ||
                    row.categorySlug.toLowerCase().includes(term) ||
                    row.sku.toLowerCase().includes(term);
            }
            return true;
        });
    }, [previewData.preview, previewFilter, previewSearch]);

    const totalPages = Math.ceil(allFiltered.length / PREVIEW_PAGE_SIZE);
    const paginatedRows = allFiltered.slice((page - 1) * PREVIEW_PAGE_SIZE, page * PREVIEW_PAGE_SIZE);
    const startItem = allFiltered.length > 0 ? (page - 1) * PREVIEW_PAGE_SIZE + 1 : 0;
    const endItem = Math.min(page * PREVIEW_PAGE_SIZE, allFiltered.length);

    useEffect(() => { setPage(1); }, [previewFilter, previewSearch]);

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Table Header Controls */}
            <div className="p-4 border-b border-slate-100 bg-slate-50/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-emerald-100 text-emerald-700 rounded-xl">
                        <Eye size={16} />
                    </div>
                    <div>
                        <h3 className="font-bold text-slate-800 text-sm">Spreadsheet Rows</h3>
                        <p className="text-xs text-slate-500">
                            Showing {allFiltered.length} of {previewData.totalRows} row{previewData.totalRows === 1 ? '' : 's'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                    {/* In-table Search */}
                    <div className="relative flex-1 sm:flex-none">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Filter preview rows..."
                            value={previewSearch}
                            onChange={e => onSearchChange(e.target.value)}
                            className="pl-8 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs w-full sm:w-48 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                        />
                        {previewSearch && (
                            <button onClick={() => onSearchChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600">
                                <X size={12} />
                            </button>
                        )}
                    </div>

                    {/* Filter Pills */}
                    <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-200">
                        {(['all', 'valid', 'warning', 'error'] as const).map(f => {
                            const count = f === 'all' ? previewData.totalRows : f === 'valid' ? previewData.validRows : f === 'warning' ? previewData.warningRows : previewData.errorRows;
                            const isSelected = previewFilter === f;
                            return (
                                <button
                                    key={f}
                                    onClick={() => onFilterChange(f)}
                                    type="button"
                                    className={clsx(
                                        'px-2.5 py-1 rounded-lg text-xs font-semibold transition-all flex items-center gap-1',
                                        isSelected
                                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-xs'
                                            : 'text-slate-500 hover:text-slate-700'
                                    )}
                                >
                                    <span>{f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}</span>
                                    <span className={clsx('text-[10px] px-1.5 py-0.2 rounded-full font-bold', isSelected ? 'bg-emerald-200/80 text-emerald-900' : 'text-slate-400')}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="min-w-full divide-y divide-slate-100 text-left">
                    <thead className="sticky top-0 bg-slate-50 z-10 border-b border-slate-200">
                        <tr>
                            <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider w-12">#</th>
                            <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Product Name</th>
                            <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Category</th>
                            <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Retail Price</th>
                            <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Cost Price</th>
                            <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">UOM</th>
                            <th className="px-4 py-3 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider w-28">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {paginatedRows.map((row) => {
                            const hasIssues = row.warnings.length > 0 || row.errors.length > 0;
                            const isExpanded = expandedRow === row.row;
                            return (
                                <React.Fragment key={row.row}>
                                    <tr
                                        onClick={() => hasIssues && onToggleExpand(isExpanded ? null : row.row)}
                                        className={clsx(
                                            'transition-colors group',
                                            row.status === 'error' ? 'bg-rose-50/30 hover:bg-rose-50/60' :
                                                row.status === 'warning' ? 'bg-amber-50/20 hover:bg-amber-50/50' :
                                                    'hover:bg-slate-50/80',
                                            hasIssues && 'cursor-pointer'
                                        )}
                                    >
                                        <td className="px-4 py-3 text-xs text-slate-400 font-mono font-semibold">{row.row}</td>
                                        <td className="px-4 py-3">
                                            <p className="text-sm font-semibold text-slate-800 truncate max-w-xs">{row.name}</p>
                                            {row.sku && row.sku !== '(auto)' && (
                                                <p className="text-[10px] text-slate-400 font-mono mt-0.5">SKU: {row.sku}</p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-600">
                                            <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-medium">
                                                {row.categorySlug || '—'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right font-bold text-emerald-700 tabular-nums">
                                            {row.retailPrice ? `₦${parseFloat(row.retailPrice).toLocaleString()}` : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-slate-600 tabular-nums font-medium">
                                            {row.costPrice ? `₦${parseFloat(row.costPrice).toLocaleString()}` : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-500 font-medium">{row.baseUom || '—'}</td>
                                        <td className="px-4 py-3 text-center">
                                            {row.status === 'valid' && (
                                                <Badge variant="success" size="sm">
                                                    <CheckCircle className="w-3 h-3 mr-1" /> Ready
                                                </Badge>
                                            )}
                                            {row.status === 'warning' && (
                                                <Badge variant="warning" size="sm">
                                                    <AlertTriangle className="w-3 h-3 mr-1" /> {row.warnings.length} Notice
                                                </Badge>
                                            )}
                                            {row.status === 'error' && (
                                                <Badge variant="danger" size="sm">
                                                    <X className="w-3 h-3 mr-1" /> {row.errors.length} Error
                                                </Badge>
                                            )}
                                        </td>
                                    </tr>

                                    {/* Inline Notice Details */}
                                    {isExpanded && (
                                        <tr>
                                            <td colSpan={7} className="px-6 py-3 bg-slate-50 border-y border-slate-200">
                                                <div className="space-y-2">
                                                    {row.errors.map((err, i) => (
                                                        <div key={i} className="flex items-start gap-2.5 p-2.5 bg-rose-50 border border-rose-200 rounded-xl text-xs">
                                                            <X size={14} className="text-rose-500 mt-0.5 flex-shrink-0" />
                                                            <div>
                                                                <span className="font-bold text-rose-800">Error: </span>
                                                                <span className="text-rose-700">{err}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {row.warnings.map((warn, i) => (
                                                        <div key={i} className="flex items-start gap-2.5 p-2.5 bg-amber-50 border border-amber-200 rounded-xl text-xs">
                                                            <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                                                            <div>
                                                                <span className="font-bold text-amber-800">Notice: </span>
                                                                <span className="text-amber-700">{warn}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>

                {paginatedRows.length === 0 && (
                    <div className="text-center py-14">
                        <Search size={36} className="text-slate-300 mx-auto mb-2" />
                        <p className="text-sm font-semibold text-slate-700">No matching products found</p>
                        <p className="text-xs text-slate-400 mt-0.5">Try clearing your search keyword or switching status tab</p>
                        <button
                            onClick={() => { onFilterChange('all'); onSearchChange(''); }}
                            className="text-xs text-emerald-600 font-bold mt-2 hover:underline"
                            type="button"
                        >
                            Reset filters
                        </button>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-xs text-slate-600">
                        Showing <strong className="text-slate-900">{startItem}</strong> to <strong className="text-slate-900">{endItem}</strong> of <strong className="text-slate-900">{allFiltered.length}</strong>
                    </p>
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => setPage(1)}
                            disabled={page === 1}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 disabled:opacity-30"
                            type="button"
                        >
                            <ChevronsLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="px-3 py-1.5 border border-slate-200 rounded-xl text-xs font-semibold disabled:opacity-40 hover:bg-slate-100 transition flex items-center gap-1"
                            type="button"
                        >
                            <ChevronLeft size={13} /> Prev
                        </button>
                        <span className="px-3 text-xs font-bold text-slate-700">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            className="px-3 py-1.5 border border-slate-200 rounded-xl text-xs font-semibold disabled:opacity-40 hover:bg-slate-100 transition flex items-center gap-1"
                            type="button"
                        >
                            Next <ChevronRight size={13} />
                        </button>
                        <button
                            onClick={() => setPage(totalPages)}
                            disabled={page >= totalPages}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 disabled:opacity-30"
                            type="button"
                        >
                            <ChevronsRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================
// INTERACTIVE STAT CARD (LIGHT MODE)
// ============================================================

function InteractiveSummaryCard({ label, value, icon: Icon, isSelected, onClick }: {
    label: string;
    value: number;
    icon: React.ElementType;
    isSelected?: boolean;
    onClick?: () => void;
}) {
    return (
        <div
            onClick={onClick}
            className={clsx(
                'bg-white rounded-2xl border-2 p-4 transition-all cursor-pointer relative overflow-hidden',
                isSelected
                    ? 'border-emerald-500 bg-emerald-50/30 shadow-xs'
                    : 'border-slate-200 hover:border-slate-300 shadow-xs'
            )}
        >
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{label}</p>
                    <p className="text-2xl font-black text-slate-900 mt-1">{value}</p>
                </div>
                <div className={clsx('p-2 rounded-xl text-emerald-700', isSelected ? 'bg-emerald-100' : 'bg-slate-100')}>
                    <Icon className="w-4 h-4" />
                </div>
            </div>
        </div>
    );
}

// ============================================================
// MAIN PAGE COMPONENT
// ============================================================

export function BulkProductUploadPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Step management
    const [currentStep, setCurrentStep] = useState<UploadStep>('upload');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [showGuideModal, setShowGuideModal] = useState(false);
    const [showStepPrimer, setShowStepPrimer] = useState(false);

    // Execution options
    const [skipDuplicates, setSkipDuplicates] = useState(true);

    // Preview state
    const [previewData, setPreviewData] = useState<BulkUploadPreview | null>(null);
    const [previewFilter, setPreviewFilter] = useState<'all' | 'valid' | 'warning' | 'error'>('all');
    const [previewSearch, setPreviewSearch] = useState('');
    const [expandedRow, setExpandedRow] = useState<number | null>(null);

    // Import state
    const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);
    const [showResultModal, setShowResultModal] = useState(false);

    const [bulkUploadState, setBulkUploadState] = useState<{
        isOpen: boolean; status: 'processing' | 'completed' | 'failed'; progress: number;
        steps: Array<{ id: string; label: string; icon: React.ElementType; status: 'pending' | 'active' | 'completed' | 'error' }>;
        result: BulkUploadResult | null; errorMessage?: string;
    }>({
        isOpen: false, status: 'processing', progress: 0,
        steps: [
            { id: 'validate', label: 'Validating spreadsheet data', icon: FileText, status: 'pending' },
            { id: 'categories', label: 'Resolving categories', icon: Package, status: 'pending' },
            { id: 'products', label: 'Inserting products & pricing', icon: Package, status: 'pending' },
            { id: 'finalize', label: 'Finalizing catalog', icon: CheckCircle, status: 'pending' },
        ],
        result: null, errorMessage: undefined,
    });

    // Preview Mutation
    const previewMutation = useMutation({
        mutationFn: async (file: File) => previewBulkUpload(user!.tenantId, file),
        onSuccess: (data) => {
            setPreviewData(data);
            setCurrentStep('preview');
            if (data.errorRows === 0 && data.warningRows === 0) {
                addToast({ type: 'success', title: 'File Analysis Complete', message: `All ${data.validRows} products are ready for import.` });
            } else if (data.errorRows > 0) {
                addToast({ type: 'warning', title: `${data.errorRows} Row(s) Need Attention`, message: `${data.validRows} valid rows can still be imported.`, duration: 7000 });
            } else {
                addToast({ type: 'info', title: 'Review Notices', message: `${data.warningRows} rows have non-blocking notices.` });
            }
        },
        onError: (err: any) => {
            addToast({ type: 'error', title: 'Analysis Failed', message: err?.response?.data?.error?.message || 'Could not parse the file.' });
            setSelectedFile(null);
            setCurrentStep('upload');
        },
    });

    // Import Mutation
    const uploadMutation = useMutation({
        mutationFn: async (file: File) => {
            setCurrentStep('importing');
            setBulkUploadState(prev => ({ ...prev, isOpen: true, status: 'processing', progress: 0, steps: prev.steps.map(s => ({ ...s, status: 'pending' as const })), result: null, errorMessage: undefined }));

            const updateProgress = (stepId: string, progress: number) => {
                setBulkUploadState(prev => ({
                    ...prev,
                    progress,
                    steps: prev.steps.map(s => {
                        if (s.id === stepId) return { ...s, status: 'active' as const };
                        const si = prev.steps.findIndex(ps => ps.id === s.id);
                        const ai = prev.steps.findIndex(ps => ps.id === stepId);
                        return si < ai ? { ...s, status: 'completed' as const } : s;
                    })
                }));
            };

            updateProgress('validate', 20);
            await new Promise(r => setTimeout(r, 400));
            updateProgress('categories', 50);

            const result = await bulkUploadProducts(user!.tenantId, file, { skipDuplicates });

            updateProgress('products', 80);
            await new Promise(r => setTimeout(r, 300));
            updateProgress('finalize', 100);

            return result;
        },
        onSuccess: (result) => {
            const hasErrors = result.hasErrors || result.errorCount > 0;
            setBulkUploadState(prev => ({
                ...prev,
                progress: 100,
                status: hasErrors ? 'failed' : 'completed',
                steps: prev.steps.map(s => ({ ...s, status: s.id === 'finalize' && hasErrors ? 'error' as const : 'completed' as const })),
                result
            }));
            setUploadResult(result);
            setCurrentStep('complete');
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['categories-flat'] });
            queryClient.invalidateQueries({ queryKey: ['product-stats'] });
            setShowResultModal(true);
        },
        onError: (err: any) => {
            setCurrentStep('complete');
            const apiError = err?.response?.data?.error;
            const errorMessage = apiError?.message || err?.message || 'Bulk upload failed.';
            setBulkUploadState(prev => ({
                ...prev,
                progress: 100,
                status: 'failed',
                errorMessage: errorMessage.substring(0, 300),
                steps: prev.steps.map(s => ({ ...s, status: s.status === 'active' ? 'error' as const : s.status }))
            }));
            addToast({ type: 'error', title: 'Upload Failed', message: errorMessage.substring(0, 200), duration: 10000 });
            setUploadResult({
                uploadId: '',
                totalRows: 0,
                successCount: 0,
                errorCount: 1,
                skippedCount: 0,
                errors: [{ row: 0, productName: 'System Error', field: 'Processing', message: errorMessage, suggestedFix: 'Verify spreadsheet column headers and try again.' }],
                hasErrors: true,
                categoriesCreated: 0,
                vendorsCreated: 0
            });
            setShowResultModal(true);
        },
    });

    // File Selection Handler
    const handleFileSelect = (file: File) => {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!['.xlsx', '.xls', '.csv'].includes(ext)) {
            addToast({ type: 'error', title: 'Unsupported File Format', message: 'Please upload an Excel (.xlsx, .xls) or CSV (.csv) file.' });
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            addToast({ type: 'error', title: 'File Size Exceeded', message: 'Maximum allowed spreadsheet size is 10MB.' });
            return;
        }
        setSelectedFile(file);
        setUploadResult(null);
        setPreviewData(null);
    };

    // Load Demo Data for Instant Exploration
    const handleLoadDemoData = () => {
        const demoCsv = `Product Name,Category Slug,Description,Brand,Retail Price,Cost Price,Tax Rate (%),SKU,Barcode,Base UOM Name,Base UOM Abbr
"Indomie Instant Noodles Chicken 70g","groceries","Chicken flavor instant noodles","Indomie",250.00,190.00,7.50,"IND-CHK-70G","899886620011","Piece","pc"
"Golden Penny Spaghetti 500g","groceries","Long spaghetti pasta","Golden Penny",850.00,680.00,7.50,"GP-SPAG-500","779886620022","Pack","pk"
"Peak Evaporated Milk 160g","dairy","Full cream evaporated milk","Peak",600.00,490.00,0.00,"PEAK-MILK-160","899886620033","Tin","tn"
"Milo Chocolate Malt Drink 500g","beverages","Energy chocolate drink","Nestle",2200.00,1850.00,7.50,"MILO-DRK-500","899886620044","Can","cn"
"Paracetamol Tablets 500mg","pharmacy","Pain relief fever reducer","Emzor",150.00,90.00,0.00,"PARA-TAB-500","899886620055","Card","cd"`;

        const blob = new Blob([demoCsv], { type: 'text/csv' });
        const file = new File([blob], 'demo-products-catalog.csv', { type: 'text/csv' });
        handleFileSelect(file);
        addToast({ type: 'info', title: 'Sample Catalog Loaded', message: 'Click "Review Spreadsheet Data" below to see the preview table.' });
    };

    const handlePreview = () => {
        if (selectedFile) previewMutation.mutate(selectedFile);
    };

    const handleStartImport = () => {
        if (selectedFile) uploadMutation.mutate(selectedFile);
    };

    const handleDownloadTemplate = async () => {
        try {
            const blob = await downloadTemplate(user!.tenantId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'etipos-products-import-template.xlsx';
            a.click();
            URL.revokeObjectURL(url);
            addToast({ type: 'success', title: 'Template Downloaded', message: 'Open in Excel, fill in your products, and upload.' });
        } catch {
            addToast({ type: 'error', title: 'Download Failed', message: 'Could not generate spreadsheet template.' });
        }
    };

    const handleReset = () => {
        setSelectedFile(null);
        setUploadResult(null);
        setPreviewData(null);
        setCurrentStep('upload');
        setShowResultModal(false);
        setBulkUploadState(prev => ({ ...prev, isOpen: false }));
    };

    // Financial calculations on preview data
    const financialSummary = useMemo(() => {
        if (!previewData) return null;
        let totalRetail = 0;
        let totalCost = 0;
        let count = 0;

        previewData.preview.forEach(row => {
            if (row.status !== 'error') {
                const retail = parseFloat(row.retailPrice || '0');
                const cost = parseFloat(row.costPrice || '0');
                if (retail > 0) {
                    totalRetail += retail;
                    totalCost += cost;
                    count++;
                }
            }
        });

        const grossProfit = totalRetail - totalCost;
        const marginPct = totalRetail > 0 ? ((grossProfit / totalRetail) * 100).toFixed(1) : '0';

        return { totalRetail, totalCost, grossProfit, marginPct, count };
    }, [previewData]);

    return (
        <div className="min-h-screen bg-slate-50/70">
            {/* Top Navigation */}
            <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-xs">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/products')}
                            className="p-2 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-600 transition-colors"
                            type="button"
                            title="Back to Products"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                    <CloudUpload className="w-5 h-5 text-emerald-600" />
                                    Product Import Hub
                                </h1>
                                <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                    Excel & CSV
                                </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {currentStep === 'upload' && 'Upload catalog spreadsheets to import products, prices, and categories'}
                                {currentStep === 'preview' && `Reviewing ${previewData?.totalRows || 0} items — verify data and prices before commit`}
                                {currentStep === 'importing' && 'Creating products, units, and categories...'}
                                {currentStep === 'complete' && 'Catalog import completed successfully'}
                            </p>
                        </div>
                    </div>

                    {/* Step Wizard Pills */}
                    <div className="flex items-center gap-3">
                        <div className="hidden md:flex items-center gap-2">
                            {[
                                { key: 'upload', label: '1. Upload' },
                                { key: 'preview', label: '2. Review' },
                                { key: 'importing', label: '3. Import' },
                            ].map((s, idx) => {
                                const isCurrent = currentStep === s.key;
                                const isPassed = (currentStep === 'preview' && s.key === 'upload') || (currentStep === 'complete');
                                return (
                                    <div key={s.key} className="flex items-center gap-2">
                                        <span className={clsx(
                                            'text-xs font-semibold px-3 py-1.5 rounded-xl transition-all',
                                            isCurrent ? 'bg-emerald-600 text-white shadow-xs' :
                                                isPassed ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                                    'bg-slate-100 text-slate-400'
                                        )}>
                                            {s.label}
                                        </span>
                                        {idx < 2 && <span className="text-slate-300">•</span>}
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => setShowGuideModal(true)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition-colors"
                            type="button"
                        >
                            <HelpCircle className="w-4 h-4 text-slate-500" />
                            <span className="hidden sm:inline">Column Guide</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">

                {/* ============================================================ */}
                {/* STEP 1: FILE UPLOAD ZONE (LIGHT THEMED)                       */}
                {/* ============================================================ */}
                {currentStep === 'upload' && (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                        {/* LEFT: Template Download & Guidance (Col 1-4) */}
                        <div className="lg:col-span-4 space-y-5">
                            {/* Official Template Card (Light Mode) */}
                            <div className="bg-white rounded-3xl border-2 border-emerald-200/80 p-6 shadow-sm relative overflow-hidden">
                                <div className="flex items-center gap-2 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-2">
                                    <Sparkles className="w-4 h-4 text-emerald-600" /> Official Excel Template
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-2">Get Started in 1 Click</h3>
                                <p className="text-xs text-slate-600 leading-relaxed mb-5">
                                    Pre-formatted with all 11 columns (Retail Price, Cost Price, Category Slug, UOM) to guarantee clean, zero-error catalog imports.
                                </p>
                                <button
                                    onClick={handleDownloadTemplate}
                                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl text-xs transition-all shadow-sm"
                                    type="button"
                                >
                                    <Download size={16} /> Download .XLSX Template
                                </button>
                            </div>

                            {/* Catalog Rules & Guidance Card */}
                            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 space-y-3.5">
                                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                                    <ShieldCheck className="w-4 h-4 text-emerald-600" /> Helpful Rules & Behavior
                                </h4>
                                <ul className="text-xs text-slate-600 space-y-2.5">
                                    <li className="flex items-start gap-2.5">
                                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                        <span><strong>Product Name</strong> must be unique in your store.</span>
                                    </li>
                                    <li className="flex items-start gap-2.5">
                                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                        <span><strong>Category Slugs:</strong> Automatically creates missing categories.</span>
                                    </li>
                                    <li className="flex items-start gap-2.5">
                                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                        <span><strong>SKU & Barcode:</strong> Auto-generated if left blank in file.</span>
                                    </li>
                                    <li className="flex items-start gap-2.5">
                                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                        <span><strong>Opening Stock:</strong> Stock counts are managed cleanly in Inventory counts.</span>
                                    </li>
                                </ul>
                                <button
                                    onClick={() => setShowGuideModal(true)}
                                    className="text-xs text-emerald-600 font-bold hover:underline pt-1 inline-block"
                                    type="button"
                                >
                                    View full column breakdown (A to K) →
                                </button>
                            </div>
                        </div>

                        {/* RIGHT: Drop Zone & Preview CTA (Col 5-12) */}
                        <div className="lg:col-span-8">
                            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col justify-between min-h-[460px]">
                                <div className="p-8 sm:p-12 flex flex-col items-center justify-center flex-1">
                                    <div
                                        onDrop={e => {
                                            e.preventDefault();
                                            setIsDragging(false);
                                            const f = e.dataTransfer.files?.[0];
                                            if (f) handleFileSelect(f);
                                        }}
                                        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                                        onDragLeave={() => setIsDragging(false)}
                                        onClick={() => !selectedFile && fileInputRef.current?.click()}
                                        className={clsx(
                                            'w-full border-2 border-dashed rounded-3xl p-10 sm:p-14 text-center transition-all cursor-pointer relative overflow-hidden flex flex-col items-center justify-center',
                                            isDragging ? 'border-emerald-500 bg-emerald-50/50 scale-[1.01]' :
                                                selectedFile ? 'border-emerald-400 bg-emerald-50/30' :
                                                    'border-slate-300 hover:border-emerald-400 hover:bg-slate-50/80'
                                        )}
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".xlsx,.xls,.csv"
                                            className="hidden"
                                            onChange={e => {
                                                const f = e.target.files?.[0];
                                                if (f) { handleFileSelect(f); e.target.value = ''; }
                                            }}
                                        />

                                        {selectedFile ? (
                                            <div className="space-y-3">
                                                <div className="w-20 h-20 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto shadow-sm">
                                                    <FileSpreadsheet className="w-10 h-10" />
                                                </div>
                                                <h3 className="text-xl font-bold text-slate-800">{selectedFile.name}</h3>
                                                <p className="text-xs text-slate-400">
                                                    {(selectedFile.size / 1024).toFixed(1)} KB · Ready to review and import
                                                </p>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedFile(null);
                                                    }}
                                                    className="text-xs text-rose-500 hover:text-rose-600 font-bold hover:underline"
                                                    type="button"
                                                >
                                                    Choose different file
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto text-slate-400">
                                                    <CloudUpload className="w-10 h-10 text-emerald-600" />
                                                </div>
                                                <h3 className="text-xl font-bold text-slate-800">
                                                    {isDragging ? 'Drop your spreadsheet here!' : 'Drop catalog file here, or browse'}
                                                </h3>
                                                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                                                    Supports Excel (.xlsx, .xls) and CSV (.csv) up to 10MB
                                                </p>
                                                <div className="flex items-center justify-center gap-2 pt-2">
                                                    <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg">.XLSX</span>
                                                    <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg">.XLS</span>
                                                    <span className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg">.CSV</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 w-full">
                                        {selectedFile ? (
                                            <button
                                                onClick={handlePreview}
                                                disabled={previewMutation.isPending}
                                                className="w-full sm:w-auto px-12 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                                                type="button"
                                            >
                                                {previewMutation.isPending ? (
                                                    <>
                                                        <Spinner size="xs" variant="white" />
                                                        Validating Rows...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Eye size={16} />
                                                        Review Spreadsheet Data
                                                    </>
                                                )}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleLoadDemoData}
                                                className="px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl font-semibold text-xs transition-colors flex items-center gap-2 shadow-xs"
                                                type="button"
                                            >
                                                <PlayCircle className="w-4 h-4 text-emerald-600" />
                                                Load Sample Spreadsheet (5 Products)
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="border-t border-slate-100 px-8 py-4 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
                                    <span>Don't have your spreadsheet ready?</span>
                                    <button
                                        onClick={handleDownloadTemplate}
                                        className="text-emerald-600 font-bold hover:underline flex items-center gap-1"
                                        type="button"
                                    >
                                        Download pre-formatted template <Download size={12} />
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                )}

                {/* ============================================================ */}
                {/* STEP 2: PREVIEW & CONFIRMATION (LIGHT MODE)                  */}
                {/* ============================================================ */}
                {currentStep === 'preview' && previewData && (
                    <div className="space-y-6">

                        {/* Interactive Metric Cards (Light Themed) */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <InteractiveSummaryCard
                                label="Total Rows"
                                value={previewData.totalRows}
                                icon={FileSpreadsheet}
                                isSelected={previewFilter === 'all'}
                                onClick={() => setPreviewFilter('all')}
                            />
                            <InteractiveSummaryCard
                                label="Valid Items"
                                value={previewData.validRows}
                                icon={CheckCircle}
                                isSelected={previewFilter === 'valid'}
                                onClick={() => setPreviewFilter('valid')}
                            />
                            <InteractiveSummaryCard
                                label="Notices"
                                value={previewData.warningRows}
                                icon={AlertTriangle}
                                isSelected={previewFilter === 'warning'}
                                onClick={() => setPreviewFilter('warning')}
                            />
                            <InteractiveSummaryCard
                                label="Errors"
                                value={previewData.errorRows}
                                icon={X}
                                isSelected={previewFilter === 'error'}
                                onClick={() => setPreviewFilter('error')}
                            />
                            <InteractiveSummaryCard
                                label="New Categories"
                                value={previewData.newCategories.length}
                                icon={Package}
                            />
                        </div>

                        {/* FINANCIAL INSIGHTS STRIP (CLEAN LIGHT CARD) */}
                        {financialSummary && financialSummary.count > 0 && (
                            <div className="bg-white border-2 border-emerald-200/90 rounded-3xl p-5 shadow-xs flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-700">
                                        <TrendingUp className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-slate-800">Catalog Financial Projection</h4>
                                        <p className="text-xs text-slate-500">
                                            Aggregated from {financialSummary.count} products with pricing in this file
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-6 flex-wrap text-right">
                                    <div>
                                        <p className="text-[10px] text-slate-500 uppercase font-semibold">Total Retail Value</p>
                                        <p className="text-lg font-black text-emerald-700">
                                            ₦{financialSummary.totalRetail.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                        </p>
                                    </div>
                                    <div className="border-l border-slate-200 pl-6">
                                        <p className="text-[10px] text-slate-500 uppercase font-semibold">Total Cost Base</p>
                                        <p className="text-lg font-black text-slate-700">
                                            ₦{financialSummary.totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                        </p>
                                    </div>
                                    <div className="border-l border-slate-200 pl-6">
                                        <p className="text-[10px] text-slate-500 uppercase font-semibold">Projected Margin</p>
                                        <p className="text-lg font-black text-emerald-600">
                                            {financialSummary.marginPct}%
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* New Categories Notice */}
                        {previewData.newCategories.length > 0 && (
                            <div className="bg-emerald-50/70 border border-emerald-200 rounded-2xl p-4 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2.5">
                                    <Package className="w-5 h-5 text-emerald-700 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs font-bold text-emerald-900">
                                            {previewData.newCategories.length} new categor{previewData.newCategories.length === 1 ? 'y' : 'ies'} will be automatically generated:
                                        </p>
                                        <div className="flex flex-wrap gap-1.5 mt-1">
                                            {previewData.newCategories.map(cat => (
                                                <Badge key={cat} variant="info" size="sm">{cat}</Badge>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* PREVIEW TABLE */}
                        <PreviewTable
                            previewData={previewData}
                            previewFilter={previewFilter}
                            previewSearch={previewSearch}
                            expandedRow={expandedRow}
                            onFilterChange={setPreviewFilter}
                            onSearchChange={setPreviewSearch}
                            onToggleExpand={setExpandedRow}
                        />

                        {/* BOTTOM ACTION BAR */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => { setCurrentStep('upload'); setPreviewData(null); }}
                                    className="px-5 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-xl transition"
                                    type="button"
                                >
                                    ← Choose Different File
                                </button>
                                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={skipDuplicates}
                                        onChange={e => setSkipDuplicates(e.target.checked)}
                                        className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                                    />
                                    <span>Skip duplicate SKUs / barcodes</span>
                                </label>
                            </div>

                            <button
                                onClick={handleStartImport}
                                disabled={uploadMutation.isPending || (previewData.importableRows || previewData.validRows + previewData.warningRows) === 0}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-xl shadow-sm transition-all disabled:opacity-50 cursor-pointer"
                                type="button"
                            >
                                <Zap size={16} />
                                Import {previewData.importableRows || previewData.validRows + previewData.warningRows} Products to Catalog
                            </button>
                        </div>
                    </div>
                )}

                {/* ============================================================ */}
                {/* STEP 3: IMPORTING STATE                                       */}
                {/* ============================================================ */}
                {currentStep === 'importing' && (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <Spinner size="xl" variant="emerald" />
                        <h3 className="text-lg font-bold text-slate-800 mt-4">Writing Products to Catalog...</h3>
                        <p className="text-xs text-slate-500 max-w-sm mt-1">
                            Creating catalog items, computing prices, and establishing category links. Please keep this tab open.
                        </p>
                    </div>
                )}

            </div>

            {/* Column Guide Modal */}
            <ColumnGuideModal isOpen={showGuideModal} onClose={() => setShowGuideModal(false)} />

            {/* Bulk Upload Progress Modal */}
            <BulkUploadProgress
                isOpen={bulkUploadState.isOpen}
                onClose={() => {
                    if (bulkUploadState.status !== 'processing') {
                        setBulkUploadState(prev => ({ ...prev, isOpen: false }));
                    }
                }}
                steps={bulkUploadState.steps}
                progress={bulkUploadState.progress}
                status={bulkUploadState.status}
                stats={{
                    totalRows: bulkUploadState.result?.totalRows || 0,
                    successCount: bulkUploadState.result?.successCount || 0,
                    errorCount: bulkUploadState.result?.errorCount || 0,
                    categoriesCreated: bulkUploadState.result?.categoriesCreated || 0
                }}
                errorMessage={bulkUploadState.errorMessage}
            />

            {/* Final Result Modal */}
            <BulkUploadResultModal
                isOpen={showResultModal}
                result={uploadResult}
                onClose={() => setShowResultModal(false)}
                onViewErrors={(uploadId) => navigate(`/products/bulk-upload/errors?uploadId=${uploadId}`)}
                onGoToProducts={() => navigate('/products')}
                onUploadAnother={handleReset}
                onDownloadReport={(uploadId) => navigate(`/products/bulk-upload/errors?uploadId=${uploadId}`)}
            />
        </div>
    );
}